import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { PondingLog } from '@/components/flood-events/types';
import { Timestamp } from 'firebase/firestore';

interface PondingMarkersProps {
    pondingLogs: PondingLog[];
    visible?: boolean;
}

// Create custom icon based on ponding depth
function createPondingIcon(depthMm: number): L.DivIcon {
    let color: string;
    let size: number;

    if (depthMm < 100) {
        color = '#22c55e'; // green
        size = 24;
    } else if (depthMm < 300) {
        color = '#eab308'; // yellow
        size = 28;
    } else if (depthMm < 500) {
        color = '#f97316'; // orange
        size = 32;
    } else {
        color = '#ef4444'; // red
        size = 36;
    }

    return L.divIcon({
        html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
        transition: transform 0.2s;
      ">
        <span style="font-size: ${size * 0.5}px;">💧</span>
      </div>
    `,
        className: 'ponding-marker',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2]
    });
}

function formatDate(date: Timestamp | Date): string {
    const d = date instanceof Date ? date : date?.toDate?.();
    if (!d) return 'Unknown date';

    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

function getSeverityLabel(depthMm: number): string {
    if (depthMm < 100) return 'Low';
    if (depthMm < 300) return 'Medium';
    if (depthMm < 500) return 'High';
    return 'Critical';
}

function getSeverityColor(depthMm: number): string {
    if (depthMm < 100) return '#22c55e';
    if (depthMm < 300) return '#eab308';
    if (depthMm < 500) return '#f97316';
    return '#ef4444';
}

export function PondingMarkers({ pondingLogs, visible = true }: PondingMarkersProps) {
    if (!visible || pondingLogs.length === 0) {
        return null;
    }

    // Filter out logs with invalid coordinates
    const validLogs = pondingLogs.filter(log => {
        const hasValidLat = typeof log.latitude === 'number' && !isNaN(log.latitude) && isFinite(log.latitude);
        const hasValidLng = typeof log.longitude === 'number' && !isNaN(log.longitude) && isFinite(log.longitude);
        
        if (!hasValidLat || !hasValidLng) {
            console.warn(`Ponding log ${log.id} has invalid coordinates:`, {
                latitude: log.latitude,
                longitude: log.longitude,
                city: log.city,
                date: log.date
            });
            return false;
        }
        return true;
    });

    console.log(`Rendering ${validLogs.length} out of ${pondingLogs.length} ponding logs on map`);

    // Custom cluster icon creator
    const createClusterCustomIcon = (cluster: any) => {
        const count = cluster.getChildCount();
        let size = 40;
        let className = 'marker-cluster-small';

        if (count >= 10) {
            size = 50;
            className = 'marker-cluster-large';
        } else if (count >= 5) {
            size = 45;
            className = 'marker-cluster-medium';
        }

        return L.divIcon({
            html: `<div style="
                width: ${size}px;
                height: ${size}px;
                background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                border: 3px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 3px 12px rgba(0,0,0,0.4);
                font-weight: bold;
                color: white;
                font-size: ${size * 0.35}px;
            ">${count}</div>`,
            className: `marker-cluster ${className}`,
            iconSize: [size, size],
        });
    };

    return (
        <>
            <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterCustomIcon}
                maxClusterRadius={50}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
            >
                {validLogs.map((log) => (
                <Marker
                    key={log.id}
                    position={[log.latitude, log.longitude]}
                    icon={createPondingIcon(log.depthMm)}
                >
                    <Popup maxWidth={320} className="ponding-popup">
                        <div style={{ backgroundColor: 'white', padding: '12px', minWidth: '240px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '12px',
                                paddingBottom: '8px',
                                borderBottom: '2px solid #e5e7eb'
                            }}>
                                <span style={{ fontSize: '24px' }}>💧</span>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{
                                        fontWeight: 'bold',
                                        margin: '0',
                                        fontSize: '16px',
                                        color: '#1f2937'
                                    }}>
                                        Ponding Event
                                    </h3>
                                    <p style={{
                                        margin: '2px 0 0 0',
                                        fontSize: '12px',
                                        color: '#6b7280'
                                    }}>
                                        {log.city}, {log.region}
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gap: '10px' }}>
                                {/* Depth and Severity */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px'
                                }}>
                                    <div>
                                        <p style={{
                                            margin: '0',
                                            fontSize: '11px',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Depth
                                        </p>
                                        <p style={{
                                            margin: '2px 0 0 0',
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            color: getSeverityColor(log.depthMm)
                                        }}>
                                            {log.depthMm}mm
                                        </p>
                                    </div>
                                    <div>
                                        <p style={{
                                            margin: '0',
                                            fontSize: '11px',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Severity
                                        </p>
                                        <p style={{
                                            margin: '2px 0 0 0',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: getSeverityColor(log.depthMm)
                                        }}>
                                            {getSeverityLabel(log.depthMm)}
                                        </p>
                                    </div>
                                </div>

                                {/* Date */}
                                <div>
                                    <p style={{
                                        margin: '0',
                                        fontSize: '11px',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Date
                                    </p>
                                    <p style={{
                                        margin: '2px 0 0 0',
                                        fontSize: '13px',
                                        color: '#374151'
                                    }}>
                                        📅 {formatDate(log.date)}
                                    </p>
                                </div>

                                {/* Reporter */}
                                {log.reporter && (
                                    <div>
                                        <p style={{
                                            margin: '0',
                                            fontSize: '11px',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Reporter
                                        </p>
                                        <p style={{
                                            margin: '2px 0 0 0',
                                            fontSize: '13px',
                                            color: '#374151'
                                        }}>
                                            👤 {log.reporter}
                                        </p>
                                    </div>
                                )}

                                {/* Location */}
                                <div>
                                    <p style={{
                                        margin: '0',
                                        fontSize: '11px',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Coordinates
                                    </p>
                                    <p style={{
                                        margin: '2px 0 0 0',
                                        fontSize: '12px',
                                        color: '#6b7280',
                                        fontFamily: 'monospace'
                                    }}>
                                        📍 {log.latitude.toFixed(5)}, {log.longitude.toFixed(5)}
                                    </p>
                                </div>

                                {/* Images */}
                                {((log.streetViewImages?.length || 0) + (log.eventViewImages?.length || 0)) > 0 && (
                                    <div style={{
                                        marginTop: '4px',
                                        paddingTop: '8px',
                                        borderTop: '1px solid #e5e7eb'
                                    }}>
                                        <p style={{
                                            margin: '0',
                                            fontSize: '12px',
                                            color: '#374151',
                                            fontWeight: '500'
                                        }}>
                                            📸 {(log.streetViewImages?.length || 0) + (log.eventViewImages?.length || 0)} image(s) attached
                                        </p>
                                    </div>
                                )}

                                {/* Source/Link */}
                                {log.source && (
                                    <div style={{
                                        marginTop: '4px',
                                        paddingTop: '8px',
                                        borderTop: '1px solid #e5e7eb'
                                    }}>
                                        <p style={{
                                            margin: '0',
                                            fontSize: '11px',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Source
                                        </p>
                                        <p style={{
                                            margin: '2px 0 0 0',
                                            fontSize: '12px',
                                            color: '#374151'
                                        }}>
                                            {log.source}
                                        </p>
                                        {log.link && (
                                            <a
                                                href={log.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#3b82f6',
                                                    textDecoration: 'underline',
                                                    display: 'inline-block',
                                                    marginTop: '4px'
                                                }}
                                            >
                                                View Source →
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Popup>
                </Marker>
                ))}
            </MarkerClusterGroup>

            <style>{`
        .ponding-marker:hover > div {
          transform: scale(1.15);
        }
        .ponding-popup .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
          background-color: white;
          opacity: 1;
        }
        .ponding-popup .leaflet-popup-tip {
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          background-color: white;
        }
      `}</style>
        </>
    );
}
